'use client';

import ToolCatalogCardGrid from '@/components/tools/ToolCatalogCardGrid';
import type { ToolCatalogLookup } from '@/lib/tool-catalog-types';
import type { ToolCatalogItem } from '@/lib/tools';
import { useEffect, useMemo, useState } from 'react';

type SolutionToolCatalogSection = {
  title?: string;
  description?: string;
  entries: ToolCatalogItem[];
};

type SolutionToolCatalogProps = {
  sections: SolutionToolCatalogSection[];
};

const TOOL_CATALOG_LOOKUP_BATCH_SIZE = 48;

export default function SolutionToolCatalog({ sections }: SolutionToolCatalogProps) {
  const [catalogLookup, setCatalogLookup] = useState<ToolCatalogLookup>({
    summaries: {},
    viewerStates: {},
  });

  const toolSlugList = useMemo(
    () =>
      Array.from(
        new Set(sections.flatMap((section) => section.entries.map((entry) => entry.slug))),
      ),
    [sections],
  );

  useEffect(() => {
    let isCancelled = false;

    async function loadCatalogLookup() {
      if (!toolSlugList.length) {
        return;
      }

      try {
        const batches = Array.from(
          { length: Math.ceil(toolSlugList.length / TOOL_CATALOG_LOOKUP_BATCH_SIZE) },
          (_, batchIndex) =>
            toolSlugList.slice(
              batchIndex * TOOL_CATALOG_LOOKUP_BATCH_SIZE,
              (batchIndex + 1) * TOOL_CATALOG_LOOKUP_BATCH_SIZE,
            ),
        );

        const responses = await Promise.all(
          batches.map(async (batch) => {
            const response = await fetch(
              `/api/tools/catalog?toolSlugs=${encodeURIComponent(batch.join(','))}`,
              {
                method: 'GET',
                cache: 'no-store',
              },
            );
            const payload = (await response.json()) as {
              success?: boolean;
              data?: ToolCatalogLookup;
            };

            if (!response.ok || !payload.success || !payload.data) {
              throw new Error('Could not load tool catalog summaries.');
            }

            return payload.data;
          }),
        );

        if (isCancelled) {
          return;
        }

        setCatalogLookup(
          responses.reduce<ToolCatalogLookup>(
            (accumulator, payload) => ({
              summaries: {
                ...accumulator.summaries,
                ...payload.summaries,
              },
              viewerStates: accumulator.viewerStates,
            }),
            {
              summaries: {},
              viewerStates: {},
            },
          ),
        );
      } catch {
        return;
      }
    }

    void loadCatalogLookup();

    return () => {
      isCancelled = true;
    };
  }, [toolSlugList]);

  return (
    <>
      {sections.map((section, index) => (
        <section
          className="overview-section solution-tool-section"
          key={`${section.title ?? 'tools'}-${index}`}
        >
          {section.title || section.description ? (
            <div className="section-heading">
              {section.title ? <h2>{section.title}</h2> : null}
              {section.description ? <p>{section.description}</p> : null}
            </div>
          ) : null}
          <ToolCatalogCardGrid
            entries={section.entries}
            summaries={catalogLookup.summaries}
            showCtaAffordance
          />
        </section>
      ))}
    </>
  );
}
